import {FC, useState, useCallback} from "react";
import {Box, BoxProps, Button, Stack} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import {BulkCourseCompletionConfirmationModal} from "./BulkCourseCompletionConfirmationModal";
import {useOwnedCohort} from "@/app2/shared/hooks";
import {useGetCohortSettings} from "@/app2/shared/data";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {BulkEnrollmentConfirmationModal} from "./BulkEnrollment/BulkEnrollmentConfirmationModal";
import {BulkUnenrollmentConfirmationModal} from "./BulkEnrollment/BulkUnenrollmentConfirmationModal";
import {EnrollCohortConfirmationModal} from "./BulkEnrollment/EnrollCohortConfirmationModal";
import {ManageDueDatesModal} from "./BulkEnrollment/components/ManageDueDatesModal";
import {useCourseEnrollmentActions} from "./BulkEnrollment/hooks/useCourseEnrollmentActions";
import {CourseEnrollmentsGrid} from "./CourseEnrollmentsGrid";

const useStyles = makeStyles()((theme) => ({
	contentBox: {
		display: "flex",
		flexDirection: "column",
		marginTop: theme.spacing(3),
		padding: theme.spacing(2),
		backgroundColor: theme.palette.background.paper,
		borderWidth: theme.borderWidth.small,
		borderColor: theme.palette.grey[100],
		borderStyle: "solid",
		borderRadius: theme.borderRadius.small
	},
	actionsRow: {
		marginBottom: theme.spacing(2)
	}
}));

interface ICustomProps {
	courseId: number | undefined;
	course?: {
		publishedAt?: Date | string | null;
	}
}

export type IProps = Omit<BoxProps, "children"> & ICustomProps;

export const EnrollmentTab: FC<IProps> = ({courseId, course, ...baseProps}) => {
	const {classes} = useStyles();
	const [bulkUnenrollConfirmationModalOpen, setBulkUnenrollConfirmationModalOpen] = useState<boolean>(false);
	const [bulkCompletionModalOpen, setBulkCompletionModalOpen] = useState<boolean>(false);
	const [bulkEnrollmentConfirmationModalOpen, setBulkEnrollmentConfirmationModalOpen] = useState<boolean>(false);
	const [enrollCohortModalOpen, setEnrollCohortModalOpen] = useState<boolean>(false);
	const [manageDueDatesModalOpen, setManageDueDatesModalOpen] = useState<boolean>(false);
	const [selected, setSelected] = useState<number[]>([]);
	const [selectedSingular, setSelectedSingular] = useState<number>();
	const ownedCohort = useOwnedCohort();
	const {data: cohortSettings} = useGetCohortSettings(ownedCohort?.id ?? 0, {
		enabled: !!ownedCohort?.id
	});
	const {t} = useSSTranslation();
	const isCoursePublished = !!course?.publishedAt;
	const {manageDueDates, isManageDueDatesPending} = useCourseEnrollmentActions(courseId ?? 0);

	const handleEnrollCohort = useCallback(() => {
		if (!ownedCohort?.id || !courseId) return;
		setEnrollCohortModalOpen(true);
	}, [ownedCohort?.id, courseId]);

	const handleEnrollmentClose = () => {
		setSelectedSingular(undefined);
		setBulkEnrollmentConfirmationModalOpen(false);
	};

	const handleUnenrollmentClose = () => {
		setSelectedSingular(undefined);
		setBulkUnenrollConfirmationModalOpen(false);
	};

	const handleEnroll = (userIds: number[]) => {
		setSelected(userIds);
		setBulkEnrollmentConfirmationModalOpen(true);
	};

	const handleUnenroll = (userIds: number[]) => {
		setSelected(userIds);
		setBulkUnenrollConfirmationModalOpen(true);
	};

	const handleMarkComplete = (userIds: number[]) => {
		setSelected(userIds);
		setBulkCompletionModalOpen(true);
	};

	const handleManageDueDates = (userIds: number[]) => {
		setSelected(userIds);
		setManageDueDatesModalOpen(true);
	};

	const handleManageDueDatesClose = () => {
		setSelectedSingular(undefined);
		setManageDueDatesModalOpen(false);
	};

	if (!ownedCohort || !courseId)
		return <Box {...baseProps} />; // Return empty box or loading indicator

	return (
		<Box className={classes.contentBox} {...baseProps}>
			{/* TODO: This will be tested after app2 is released */}
			{/* <Stack direction="row" className={classes.actionsRow} spacing={2}>
				<Button
					variant="outlined"
					onClick={handleEnrollCohort}
					disabled={!isCoursePublished}
				>
					{t("enroll_cohort_button", "Enroll cohort")}
				</Button>
			</Stack> */}
			<CourseEnrollmentsGrid
				courseId={courseId}
				cohortId={ownedCohort.id}
				organization={cohortSettings}
				isCoursePublished={isCoursePublished}
				selected={selected}
				setSelected={setSelected}
				setSelectedSingular={setSelectedSingular}
				handleEnroll={handleEnroll}
				handleUnenroll={handleUnenroll}
				handleMarkComplete={handleMarkComplete}
				handleManageDueDates={handleManageDueDates}
			/>

			{/* Legacy modal for completion */}
			{/* New App2 bulk unenrollment modal */}
			<BulkUnenrollmentConfirmationModal
				open={bulkUnenrollConfirmationModalOpen}
				onClose={handleUnenrollmentClose}
				onSucceeded={() => setSelected([])}
				selectedUserIds={(selectedSingular ? [selectedSingular] : selected).filter(
					(id) => typeof id === "number"
				) as number[]}
				courseId={courseId}
			/>
			<BulkCourseCompletionConfirmationModal
				open={bulkCompletionModalOpen}
				onClose={() => {
					setSelected([]);
					setSelectedSingular(undefined);
					setBulkCompletionModalOpen(false);
				}}
				selectedUserIds={(selectedSingular ? [selectedSingular] : selected).filter(
					(id): id is number => typeof id === "number"
				)}
				courseId={courseId}
			/>

			{/* New App2 bulk enrollment modal */}
			<BulkEnrollmentConfirmationModal
				open={bulkEnrollmentConfirmationModalOpen}
				onClose={handleEnrollmentClose}
				selectedUserIds={(selectedSingular ? [selectedSingular] : selected).filter(
					(id) => typeof id === "number"
				) as number[]}
				courseId={courseId}
			/>

			<EnrollCohortConfirmationModal
				open={enrollCohortModalOpen}
				onClose={() => setEnrollCohortModalOpen(false)}
				courseId={courseId}
				cohortId={ownedCohort.id}
			/>

			<ManageDueDatesModal
				open={manageDueDatesModalOpen}
				onClose={handleManageDueDatesClose}
				isPending={isManageDueDatesPending}
				onApply={({mode, dueDate}) => {
					const userIds = (selectedSingular ? [selectedSingular] : selected).filter(
						(id) => typeof id === "number"
					) as number[];
					manageDueDates(
						{userIds, mode, dueDate},
						{
							onSuccess: () => {
								setSelected([]);
								handleManageDueDatesClose();
							}
						}
					);
				}}
			/>
		</Box>
	);
};
